import Proj1      from "../assets/proj1.png";
import Proj2      from "../assets/proj2.png";
import AzureImg   from "../assets/azure.jpg";
import QradarImg  from "../assets/qradar.jpg";
import SplunkImg  from "../assets/splunk.jpg";
import SoarImg    from "../assets/soar.jpg";
import VpnImg     from "../assets/openvpn.jpg";
import ScriptsImg from "../assets/scripts.jpg";
import BlogImg    from "../assets/blog.jpg";

export const ProjectList = [

  // ── CYBER ────────────────────────────────────────────────────────────────

  {
    name:     "CyberShield OPs",
    image:    ScriptsImg,
    category: "cyber",
    skills:   ["C#", ".NET", "SOC Simulation", "Rule-Based Detection", "Incident Response"],
    description: "Full-stack cybercrime detection and response platform simulating a lightweight SOC — ingests attack data, detects suspicious behaviour via rules, and triggers response workflows.",
    problem:  "Security teams need hands-on SOC tooling that mirrors real detection pipelines without the cost of enterprise SIEM licences.",
    approach: "Built a C# platform that ingests simulated attack events, runs configurable detection rules, and surfaces alerts with automated response actions — end-to-end SOC simulation.",
    highlights: [
      "Event ingestion pipeline handling structured attack simulation data",
      "Rule engine for behaviour-based anomaly detection",
      "Incident dashboard with alert severity classification",
      "Automated response triggers on rule match (isolate, notify, log)",
    ],
    impact: "Provides a self-contained SOC lab demonstrating full detection-to-response coverage in a single platform.",
    github: "https://github.com/thumbiko/CyberShield-OPs",
  },

  {
    name:     "Azure SOC Honeynet",
    image:    AzureImg,
    category: "cyber",
    skills:   ["Microsoft Sentinel", "KQL", "Log Analytics", "NSG Hardening", "Live Threat Mapping"],
    description: "Cloud-based honeynet in Azure with real-time attack detection, KQL-driven threat mapping, and structured NSG hardening.",
    problem:  "Most security teams lack visibility into how attackers move in live cloud environments before hardening is applied.",
    approach: "Deployed intentionally exposed Azure VMs to attract live traffic, ingested all logs into a Log Analytics Workspace, and used Sentinel to surface attack patterns with KQL.",
    highlights: [
      "Configured Log Analytics workspace ingesting VM, NSG, and Azure AD logs",
      "Built Sentinel workbooks and geo attack-map queries in KQL",
      "Measured security metrics before and after NSG hardening — incidents down 90%+",
      "Documented full NIST 800-53 remediation steps",
    ],
    impact: "Demonstrated measurable reduction in attack surface through structured hardening, producing a repeatable SOC lab methodology.",
    github: "https://github.com/thumbiko/Azure-SOC-Honeynet",
  },

  {
    name:     "SOC Automation Portfolio",
    image:    SoarImg,
    category: "cyber",
    skills:   ["Shuffle SOAR", "TheHive", "Wazuh", "Python", "Automated Incident Response"],
    description: "End-to-end SOAR pipeline automating alert triage, IoC enrichment, and case creation from detection to analyst notification.",
    problem:  "Manual incident triage creates analyst fatigue and slows response times — alert volume exceeds human capacity.",
    approach: "Built a Shuffle SOAR workflow ingesting Wazuh alerts, enriching IoCs via VirusTotal, creating TheHive cases, and notifying analysts automatically.",
    highlights: [
      "Wazuh as detection source feeding structured events into Shuffle",
      "Python enrichment playbooks for IP and hash reputation lookups",
      "Automated TheHive case creation with severity mapping",
      "Reduced mean time to acknowledge (MTTA) to under 30 seconds",
    ],
    impact: "Fully automated the first two stages of incident response, freeing analysts for investigation rather than triage.",
    github: "https://github.com/thumbiko/soc-automation-portfolio",
  },

  {
    name:     "Biko Pen Test Labs",
    image:    QradarImg,
    category: "cyber",
    skills:   ["Penetration Testing", "Kali Linux", "Burp Suite", "Nmap", "Exploit Development"],
    description: "Structured directory of offensive security labs covering recon, exploitation, post-exploitation, and reporting.",
    problem:  "Penetration testing skills degrade without structured, hands-on lab environments to practice against.",
    approach: "Built a lab library of scoped pen test scenarios across web apps, networks, and Active Directory — each with methodology notes and findings documentation.",
    highlights: [
      "Web application testing labs (OWASP Top 10 coverage)",
      "Network recon and exploitation walkthroughs",
      "Active Directory attack chains (Kerberoasting, Pass-the-Hash)",
      "Structured report templates for each engagement type",
    ],
    impact: "A living reference for offensive security techniques, documented to professional reporting standards.",
    github: "https://github.com/thumbiko/Biko-Pen-Test",
  },

  {
    name:     "Log Monitoring — ELK Stack",
    image:    SplunkImg,
    category: "cyber",
    skills:   ["Elasticsearch", "Logstash", "Kibana", "Log Ingestion", "Threat Dashboards"],
    description: "Centralised log monitoring pipeline using the ELK stack — ingest, parse, and visualise security events across infrastructure.",
    problem:  "Dispersed logs across servers provide no unified view of security posture or anomaly detection.",
    approach: "Deployed Elasticsearch, Logstash, and Kibana to build a centralised SIEM-like pipeline with custom Kibana dashboards for threat visibility.",
    highlights: [
      "Logstash pipelines for parsing syslog, auth, and web server logs",
      "Elasticsearch index mappings optimised for security event querying",
      "Kibana dashboards for failed login patterns, geo traffic, and anomaly spikes",
      "Alerting rules for threshold-based event detection",
    ],
    impact: "Delivered a full open-source SIEM alternative with production-ready dashboards and detection logic.",
    github: "https://github.com/thumbiko/log-monitoring-elk-stack",
  },

  // ── DEVOPS ───────────────────────────────────────────────────────────────

  {
    name:     "CI/CD Docker & Jenkins Pipeline",
    image:    Proj2,
    category: "devops",
    skills:   ["Jenkins", "Docker", "Python", "CI/CD", "Pipeline Automation"],
    description: "Automated CI/CD pipeline with Jenkins and Docker — build, test, and deploy Python applications on every commit.",
    problem:  "Manual build and deployment processes introduce human error, slow release cycles, and inconsistent environments.",
    approach: "Built a Jenkins pipeline that triggers on git push, runs tests inside Docker containers, and deploys clean images — fully automated from commit to production.",
    highlights: [
      "Jenkinsfile defining multi-stage pipeline (build → test → deploy)",
      "Dockerised application environments for consistent builds",
      "Automated test execution with pass/fail gating before deploy",
      "Parameterised pipeline supporting multiple environment targets",
    ],
    impact: "Zero-touch deployment pipeline reducing release time and eliminating environment inconsistency across stages.",
    github: "https://github.com/thumbiko/cicd-docker-jenkins-pipeline",
  },

  {
    name:     "AWS Infrastructure Automation",
    image:    VpnImg,
    category: "devops",
    skills:   ["AWS", "Terraform / IaC", "EC2", "IAM", "Infrastructure Automation"],
    description: "Infrastructure-as-Code automation for provisioning and managing AWS resources with repeatability and security built in.",
    problem:  "Manually provisioned cloud infrastructure is fragile, undocumented, and impossible to reproduce consistently.",
    approach: "Automated AWS resource provisioning using IaC tooling — defining EC2, IAM roles, security groups, and networking in version-controlled configuration.",
    highlights: [
      "Modular infrastructure definitions for EC2 and networking layers",
      "IAM roles and policies provisioned with least-privilege defaults",
      "Security group rules managed as code — no manual console changes",
      "Repeatable teardown and rebuild cycles for clean environments",
    ],
    impact: "Fully reproducible cloud infrastructure deployable in minutes, with security controls codified from day one.",
    github: "https://github.com/thumbiko/aws-infrastructure-automation",
  },

  // ── SOFTWARE ─────────────────────────────────────────────────────────────

  {
    name:     "LiquiWise",
    image:    Proj1,
    category: "software",
    skills:   ["TypeScript", "Next.js", "AI Integration", "Financial Platform", "Full Stack"],
    description: "AI-powered fintech platform providing intelligent financial insights, portfolio analysis, and liquidity management tooling.",
    problem:  "Individual investors lack access to the analytical tools available to institutional finance teams.",
    approach: "Built a TypeScript/Next.js platform integrating LLM-driven analysis to surface actionable financial insights — with a clean, fast UI designed for non-technical users.",
    highlights: [
      "LLM integration for natural-language financial Q&A over portfolio data",
      "Real-time data processing and visualisation layer",
      "TypeScript throughout — strict typing for financial data integrity",
      "Scoped AI feature design: one capability per user workflow",
    ],
    impact: "Bridges the gap between institutional-grade financial analysis and the individual investor — live and in production.",
    github: "https://github.com/thumbiko/LiquiWise",
  },

  {
    name:     "Secure Web App Project",
    image:    BlogImg,
    category: "software",
    skills:   ["JavaScript", "Node.js", "Express", "JWT Auth", "OWASP Security"],
    description: "Security-first web application demonstrating OWASP best practices — hardened auth, input validation, and secure API design.",
    problem:  "Most web app tutorials teach functionality without security — leaving common vulnerabilities like XSS, IDOR, and broken auth in production.",
    approach: "Built a web application with security as the primary constraint — JWT auth with httpOnly cookies, parameterised queries, input sanitisation, and OWASP-mapped threat modelling.",
    highlights: [
      "JWT authentication with refresh token rotation",
      "Input validation and sanitisation middleware (XSS, SQLi prevention)",
      "CORS and CSP headers configured for production hardening",
      "OWASP Top 10 threat model documented alongside the codebase",
    ],
    impact: "A reference implementation demonstrating that security and developer experience are not mutually exclusive.",
    github: "https://github.com/thumbiko/Secure_Web_App_Proj",
  },

];